import { v4 } from 'uuid';

import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { useMockAiSuggestions } from '@/views/operatingcostinvoices/composables/useMockAiSuggestions';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import {
  AiJobService,
  type OperatingCostInvoiceAiJobResultDto,
  OperatingCostInvoiceLineItemV1Dto,
  OperatingCostInvoiceV1Dto,
} from '@/openapi';

export interface AiSuggestionsService {
  getAiSuggestionsForDocument: (
    ticketId?: string | null,
    documentId?: string | null,
  ) => Promise<OperatingCostInvoiceSimple[]>;
}

export interface AiSuggestionsOptions {
  mode?: 'auto' | 'live' | 'mock';
}

type ExtendedAiResult = OperatingCostInvoiceAiJobResultDto &
  Partial<{
    pointOfSaleId: string;
    pointOfSaleName: string;
    pointOfSale: {
      id?: string | null;
      name?: string | null;
    };
    contractId: string;
    contractName: string;
    customerPrepaymentNet: number | null;
    customerPrepaymentGross: number | null;
    statedCustomerPrepaymentNet: number | null;
    settlementResultNet: number | null;
    settlementResultGross: number | null;
    costsNet: number | null;
    vatRate: number | null;
    currency: OperatingCostInvoiceV1Dto.currency | null;
    isHeatingCostsCompliantHKVO: boolean | null;
    lineItems: Array<
      Partial<OperatingCostInvoiceLineItemSimple> & {
        status?: string | null;
      }
    >;
  }>;

function asOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function normalizeLineItemStatus(
  status: unknown,
): OperatingCostInvoiceLineItemV1Dto.status | undefined {
  if (status === OperatingCostInvoiceLineItemV1Dto.status.CLEAR) {
    return OperatingCostInvoiceLineItemV1Dto.status.CLEAR;
  }

  if (status === OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR) {
    return OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR;
  }

  if (typeof status === 'string') {
    const normalized = status.trim().toLowerCase();
    if (normalized === 'clear') {
      return OperatingCostInvoiceLineItemV1Dto.status.CLEAR;
    }
    if (normalized === 'unclear') {
      return OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR;
    }
  }

  return undefined;
}

function mapLineItems(
  lineItems: ExtendedAiResult['lineItems'],
): OperatingCostInvoiceLineItemSimple[] {
  if (!Array.isArray(lineItems)) {
    return [];
  }

  return lineItems.map((item) => ({
    id: v4(),
    version: 0,
    areaDesignation: '',
    nameOnStatement: asNonEmptyString(item.nameOnStatement) ?? '',
    sfmPositionId: asNonEmptyString(item.sfmPosition?.id) ?? '',
    sfmPositionName: asNonEmptyString(item.sfmPosition?.name) ?? '',
    isFlatRate: asOptionalBoolean(item.isFlatRate) ?? false,
    totalAmount: asOptionalNumber(item.totalAmount),
    customerAmount: asOptionalNumber(item.customerAmount),
    totalArea: asOptionalNumber(item.totalArea),
    customerArea: asOptionalNumber(item.customerArea),
    status: normalizeLineItemStatus(item.status),
  }));
}

function mapAiResultToInvoiceSimple(
  result: OperatingCostInvoiceAiJobResultDto,
  ticketId: string,
): OperatingCostInvoiceSimple {
  const extendedResult = result as ExtendedAiResult;

  const pointOfSaleId =
    asNonEmptyString(extendedResult.pointOfSale?.id) ??
    asNonEmptyString(extendedResult.pointOfSaleId) ??
    '';
  const pointOfSaleName =
    asNonEmptyString(extendedResult.pointOfSale?.name) ??
    asNonEmptyString(extendedResult.pointOfSaleName);

  const invoice: OperatingCostInvoiceSimple = {
    id: v4(),
    version: 0,
    pointOfSaleId,
    pointOfSaleName,
    contractId: asNonEmptyString(extendedResult.contractId) ?? '',
    contractName: asNonEmptyString(extendedResult.contractName),
    issuerLegalName: result.issuerLegalName ?? '',
    issueDate: result.issueDate ?? '',
    periodStart: result.periodStart ?? '',
    periodEnd: result.periodEnd ?? '',
    documentReviewDeadline: result.documentReviewDeadline ?? undefined,
    objectionDeadline: result.objectionDeadline ?? undefined,
    internalObjectionDeadline: result.internalObjectionDeadline ?? undefined,
    customerPrepaymentNet: asOptionalNumber(extendedResult.customerPrepaymentNet),
    customerPrepaymentGross: asOptionalNumber(extendedResult.customerPrepaymentGross),
    statedCustomerPrepaymentNet: asOptionalNumber(extendedResult.statedCustomerPrepaymentNet),
    settlementResultNet: asOptionalNumber(extendedResult.settlementResultNet),
    settlementResultGross: asOptionalNumber(extendedResult.settlementResultGross),
    costsNet: asOptionalNumber(extendedResult.costsNet),
    currency: extendedResult.currency ?? OperatingCostInvoiceV1Dto.currency.EUR,
    vatRate: asOptionalNumber(extendedResult.vatRate) ?? 19,
    isHeatingCostsCompliantHKVO:
      asOptionalBoolean(extendedResult.isHeatingCostsCompliantHKVO) ?? false,
    areas: [],
    lineItems: mapLineItems(extendedResult.lineItems),
    ticketId,
  };

  return invoice;
}

export function useAiSuggestions(options: AiSuggestionsOptions = {}): AiSuggestionsService {
  const mockService = useMockAiSuggestions();

  const getAiSuggestionsForDocument = async (
    ticketId?: string | null,
    documentId?: string | null,
  ): Promise<OperatingCostInvoiceSimple[]> => {
    if (!ticketId || !documentId) {
      return [];
    }

    const results = await AiJobService.aiJobControllerGetOperatingCostInvoiceResultsV1(
      ticketId,
      documentId,
    );

    return (results ?? []).map((result) => mapAiResultToInvoiceSimple(result, ticketId));
  };

  const envWantsMock =
    String(import.meta.env.VITE_USE_MOCK_AI_SUGGESTIONS ?? '').toLowerCase() === 'true';

  const resolvedMode = options.mode ?? 'auto';
  const shouldUseMock = resolvedMode === 'mock' || (resolvedMode === 'auto' && envWantsMock);

  if (shouldUseMock) {
    return mockService;
  }

  return {
    getAiSuggestionsForDocument,
  };
}
